/* eslint-disable */

import { csContext } from "../../services/cs-context";
import axios from "axios";
import {
  OrgSoloReportApiModel,
  OrgViewpointsReportApiModel,
  ShortOrganisationDetailsApiModel,
  SoloReportApiModel,
  TeamApiModel,
  TeamSoloReportApiModel,
  TeamViewpointsReportApiModel,
  UserDetailsApiModel,
  UserTagDataApiModel,
  ViewpointsReportApiModel,
} from "api-models";
import leaderTypes from "./leader-types";
import strengthItemIndicators from "./strength-item-indicators";
import strengthItemsLegacy from "./strength-items-legacy";
import strengthItemLongDefinitions from "./strength-item-long-definitions";
import strengthsAndGaps1 from "./strengths-and-gaps-1";
import chat, { CompletionMessage, RawChat } from "./chat";
import { CmsDocumentTypes } from "../../services/cms/cms-type-configuration";
import cms from "../../services/cms/cms";
import { getText } from "../../components/smart-search/process-situations";
import { handleApiError, variableErrors } from "./variable-error-helpers";
import {
  formatIndicatorsFromCustomReport,
  indicatorFactors,
  formatIndicators,
} from "./indicator-helpers";
import smartTextBoxStore, { getTextBox } from "../../stores/smart-text-box-store";
import getTeamGoalVariables from "./team-goal-variables";

export type ReportModel =
  | SoloReportApiModel
  | ViewpointsReportApiModel
  | TeamSoloReportApiModel
  | TeamViewpointsReportApiModel
  | OrgSoloReportApiModel
  | OrgViewpointsReportApiModel;

type FormattedReportReturn = ReturnType<typeof formatReportData>;

export type SubjectUserResponse = {
  subjectUserModel: UserDetailsApiModel;
  subjectUserTeamsList: TeamApiModel[];
};

export type SubjectTeamResponse = {
  subjectTeamModel: TeamApiModel;
  subjectOrgModel: ShortOrganisationDetailsApiModel;
};

type SubjectOrg = {
  name: string;
  solo: () => Promise<string | FormattedReportReturn>;
  viewpoints: () => Promise<string | FormattedReportReturn>;
};

type SubjectTeam = ReturnType<typeof getTeam>;

type FormattedDataTags = ReturnType<typeof formatDataTags>;

type SubjectUser = {
  name: string;
  first_name: string;
  last_name: string;
  teams: SubjectTeam[] | string;
  org: SubjectOrg | string;
  solo: () => Promise<string | FormattedReportReturn>;
  viewpoints: () => Promise<string | FormattedReportReturn>;
  data_tags: () => Promise<string | FormattedDataTags>;
};

export const formatDataTags = (dataTags: UserTagDataApiModel) => {
  return {
    tagged_teams: dataTags.taggedTeams ?? "",
    primary_tagged_team: dataTags.primaryTaggedTeam ?? "",
    job_title: dataTags.jobTitle ?? "",
    role_description: dataTags.roleDescription ?? "",
  };
};

export const formatReportData = (report: ReportModel, type: string) => {
  if (!report) {
    return `(${type} report has not been completed)`;
  }
  let fits = report.fits.map((f) => ({
    fit_percentage: f.fitPercentage,
    fit_strength: f.fitString,
    leader_type: {
      _id: f.leaderTypeId.toLowerCase(),
      ...leaderTypes[f.leaderTypeId.toLowerCase()],
    },
    title: `${f.leaderTypeId} - ${f.fitPercentage}% fit - ${f.fitString}`,
  }));
  fits.sort((a, b) => b.fit_percentage - a.fit_percentage);

  const isSoloReportWithIndicatorsMapped =
    "indicatorRatings" in report && report.indicatorRatings.length > 0;
  const isViewpointsReport = !("indicatorRatings" in report);

  return {
    name: `${type} report on ${report.subjectName}`,
    report_type: type,
    subject_name: report.subjectName,
    subject_first_name: (report as SoloReportApiModel).subjectFirstName,
    subject_last_name: (report as SoloReportApiModel).subjectLastName,
    reviewer_first_name: (report as SoloReportApiModel).reviewerFirstName,
    reviewer_last_name: (report as SoloReportApiModel).reviewerLastName,
    leader_type_fits: fits,
    strength_ratings: report.strengthRatings.map((sr) => ({
      z_score: sr.zScore,
      raw_rating: `${Math.trunc(sr.rawRating!)}%`,
      ...getStrength(sr.strength.name),
    })),
    ...(isViewpointsReport
      ? {}
      : {
          personal_indicators: isSoloReportWithIndicatorsMapped
            ? formatIndicators(report as SoloReportApiModel, indicatorFactors.PERSONAL)
            : "no personal indicators",
        }),
    ...(isViewpointsReport
      ? {}
      : {
          team_indicators: isSoloReportWithIndicatorsMapped
            ? formatIndicators(report as SoloReportApiModel, indicatorFactors.TEAM)
            : formatIndicatorsFromCustomReport(
                (report as SoloReportApiModel).customStrengthRatings,
                "TEAM",
              ) ?? "no team indicators",
        }),
    ...(isViewpointsReport
      ? {}
      : {
          org_indicators: isSoloReportWithIndicatorsMapped
            ? formatIndicators(report as SoloReportApiModel, indicatorFactors.ORG)
            : formatIndicatorsFromCustomReport(
                (report as SoloReportApiModel).customStrengthRatings,
                "ORG",
              ) ?? "no org indicators",
        }),
  };
};

let cmsStructure = {
  resources: { type: CmsDocumentTypes.CmsResource, examples: ["working-in-iterations"] },
  personal_outcomes: { type: CmsDocumentTypes.CmsPersonalGoal, examples: ["growth"] },
  personal_pathways: { type: CmsDocumentTypes.CmsPersonalPathway },
  personal_pathway_steps: { type: CmsDocumentTypes.CmsPersonalPathwayStep },
  team_outcomes: { type: CmsDocumentTypes.CmsTeamGoal },
  team_pathways: { type: CmsDocumentTypes.CmsTeamPathway },
  team_pathway_steps: { type: CmsDocumentTypes.CmsTeamPathwayStep },
  session_with_slides: { type: CmsDocumentTypes.CmsSessionWithSlides },

  app_quotes: {
    type: CmsDocumentTypes.CmsQuote,
  },
  custom_strength_items: {
    type: CmsDocumentTypes.CmsCustomStrengthItem,
  },
  app_carousels: { type: CmsDocumentTypes.CmsCarousel },
  app_info_pages: { type: CmsDocumentTypes.CmsInfoPage },
  // sessions: {type: CmsDocumentTypes.CmsSessionWithSlides},
  // team_image_lists: { type: CmsDocumentTypes.CmsTeamImageList },
  // variable_items: { type: CmsDocumentTypes.CmsVariable },
  team_effectiveness_reviews: { type: CmsDocumentTypes.CmsTeamEffectivenessReview },
  // strings: { type: CmsDocumentTypes},
  // link_resources: {},
  app_develop_pages: { type: CmsDocumentTypes.CmsDevelopPage },
  // monthly_team_themes: {},
  // this_weeks_focuses: {},
  // playlists: { type: CmsDocumentTypes.CmsResourcePlaylist },
  // themes_pages: {},
  // themes: {},
  // pathway_wheels: {},
  // cancellation_policies: {},
  // subscription_policies: {},
  leader_types: { type: CmsDocumentTypes.CmsLeaderType },
  // custom_strength_mapping: {},
  // old_pathway_steps: {},

  develop_plans: { type: CmsDocumentTypes.CmsDevelopPlan },

  strength_items: { type: CmsDocumentTypes.CmsStrengthItem },

  packages: { type: CmsDocumentTypes.CmsPackage },
  // digital_packages: {},
  // smart_callout_variables: { type: CmsDocumentTypes.CmsSmartCallout },
  // smart_search_prompts: { type: CmsDocumentTypes.CmsSmartSearchPrompts },
  reports: { type: CmsDocumentTypes.CmsReport, examples: ["team-effectiveness-report"] },
} as { [x: string]: { type: CmsDocumentTypes; examples?: string[] } };

function getStrength(strengthName: string) {
  return {
    name: strengthName,
    as_strength: strengthsAndGaps1[strengthName].asStrength,
    as_gap: strengthsAndGaps1[strengthName].asGap,
    positive_indicators: strengthItemIndicators[strengthName].positiveIndicators,
    negative_indicators: strengthItemIndicators[strengthName].negativeIndicators,
    definition: strengthItemsLegacy.find((l) => l.name === strengthName)?.definition,
    description: strengthItemsLegacy.find((l) => l.name === strengthName)?.description,
    factor: strengthItemsLegacy.find((l) => l.name === strengthName)?.factor,
    long_definition: strengthItemLongDefinitions[strengthName],
  };
}

let getOrg = (organisation: ShortOrganisationDetailsApiModel) => {
  return organisation
    ? {
        name: organisation.name,
        solo: async () => {
          try {
            var report = (await axios.get("/api/reports/org-solo/" + organisation.id))
              .data as OrgSoloReportApiModel;
            return formatReportData(report, "Organisation Solo");
          } catch (e) {
            return handleApiError(e);
          }
        },
        viewpoints: async () => {
          try {
            var report = (await axios.get("/api/reports/org-viewpoints/" + organisation.id))
              .data as OrgViewpointsReportApiModel;
            return formatReportData(report, "Organisation Viewpoints");
          } catch (e) {
            return handleApiError(e);
          }
        },
      }
    : variableErrors.org.error;
};

let getTeam = (subjectTeam?: TeamApiModel, subjectOrg?: ShortOrganisationDetailsApiModel) => {
  const teamToFormat = subjectTeam ?? csContext.currentTeam;
  const team = teamToFormat
    ? {
        name: teamToFormat.name,
        ...(subjectOrg ? { org: getOrg(subjectOrg) } : {}),
        solo: async () => {
          try {
            var report = (await axios.get("/api/reports/team-solo/" + teamToFormat.id))
              .data as SoloReportApiModel;
            return formatReportData(report, "Team Solo");
          } catch (e) {
            return handleApiError(e);
          }
        },
        viewpoints: async () => {
          try {
            var report = (await axios.get("/api/reports/team-viewpoints/" + teamToFormat.id))
              .data as ViewpointsReportApiModel;
            return formatReportData(report, "Team Viewpoints");
          } catch (e) {
            return handleApiError(e);
          }
        },
        goals: async () => {
          try {
            return await getTeamGoalVariables(teamToFormat.id!);
          } catch (e) {
            return "Error getting team outcomes";
          }
        },
      }
    : variableErrors.team.error;

  return team;
};

const getSubjectTeams = (teams: TeamApiModel[]): SubjectTeam[] | string => {
  if (!teams.length) return variableErrors.team.error;

  return teams.map((team) => {
    return getTeam(team);
  });
};

const getSubject = (subjectUser: SubjectUserResponse): SubjectUser | string => {
  const subject = subjectUser.subjectUserModel;
  const subjectTeams = subjectUser.subjectUserTeamsList;

  return subject
    ? {
        name: `${subject.firstName} ${subject.lastName}`,
        first_name: subject.firstName,
        last_name: subject.lastName,
        teams: getSubjectTeams(subjectTeams),
        org: getOrg(subject.organisation),
        data_tags: async () => {
          try {
            const dataTags = (await axios.get(`/api/UserTagData/GetAllTags/${subject.id}`))
              .data as UserTagDataApiModel;
            return formatDataTags(dataTags);
          } catch (e) {
            return handleApiError(e);
          }
        },
        solo: async () => {
          try {
            const report = (await axios.get(`/api/reports/solo/${subject.id}`))
              .data as SoloReportApiModel;
            return formatReportData(report, "Solo");
          } catch (e) {
            return handleApiError(e);
          }
        },
        viewpoints: async () => {
          try {
            const report = (await axios.get(`/api/reports/viewpoints/${subject.id}`))
              .data as ViewpointsReportApiModel;
            return formatReportData(report, "Viewpoints");
          } catch (e) {
            return handleApiError(e);
          }
        },
      }
    : variableErrors.subject.error;
};

export type DataContextOptions = {
  subjectUser?: SubjectUserResponse;
  subjectTeam?: SubjectTeamResponse;
  subjectOrg?: ShortOrganisationDetailsApiModel;
};

export function getDataContext(options: DataContextOptions = {}) {
  var cmsObj = {} as {
    [x in CmsDocumentTypes]: {};
  };
  for (let name of Object.keys(cmsStructure)) {
    let getter = async (uid: string) => {
      let doc = await cms.getByUid(typeName, uid);
      let text = getText(doc);
      let joined = text.join("\n");
      let split = joined.split(" ").slice(0, 2000);
      if (split.length === 2000) {
        split.push("\n(truncated)");
      }
      return split.join(" ");
    };
    let typeName = cmsStructure[name].type;
    let obj = {
      _get: getter,
    };
    for (let example of cmsStructure[name].examples ?? []) {
      obj[example] = () => getter(example);
    }
    cmsObj[name] = obj;
  }

  let textBoxes = {
    _get: (uid: string) =>
      getTextBox(uid).then((x) => x || "< The user hasn't written anything here >"),
  };

  let strengths = { _text: Object.keys(strengthsAndGaps1).join("\n") };

  for (let sId of Object.keys(strengthsAndGaps1)) {
    let str = getStrength(sId);
    let kebab = sId.toLowerCase().replace(/[ -]/g, "_");
    strengths[kebab] = str;
  }

  let user = csContext.user;
  let team = getTeam();
  let org = getOrg(csContext.user.organisation);
  let reader = user
    ? {
        name: user.firstName + " " + user.lastName,
        first_name: user.firstName,
        last_name: user.lastName,
        org: org,
        team: team,
        text_boxes: textBoxes,
        solo: async () => {
          try {
            var report = (await axios.get("/api/reports/solo/" + user.id))
              .data as SoloReportApiModel;
            return formatReportData(report, "Solo");
          } catch (e) {
            return handleApiError(e);
          }
        },
        viewpoints: async () => {
          try {
            var report = (await axios.get("/api/reports/viewpoints/" + user.id))
              .data as ViewpointsReportApiModel;
            return formatReportData(report, "Viewpoints");
          } catch (e) {
            return handleApiError(e);
          }
        },
      }
    : undefined;

  const subjectUser = options.subjectUser ? getSubject(options.subjectUser) : "No user selected";

  const subjectTeam = options.subjectTeam
    ? getTeam(options.subjectTeam.subjectTeamModel, options.subjectTeam.subjectOrgModel)
    : "No team selected";
  const subjectOrg = options.subjectOrg ? getOrg(options.subjectOrg) : "No organisation selected";

  return {
    reader: reader || variableErrors.user.error,
    subject_user: subjectUser,
    subject_team: subjectTeam,
    subject_org: subjectOrg,
    leader_types: {
      _name: Object.values(leaderTypes)
        .map((v) => v.title)
        .join("\n"),
      ...leaderTypes,
    },
    // strengths,
    cms: cmsObj,
  };
}

export async function getVarList(dataContext: any, path: string = "") {
  let outputs = [] as {
    path: string;
    data: string;
  }[];
  if (!dataContext) {
    throw new Error("dataContext null or undefined");
  }

  for (let k of Object.keys(dataContext)) {
    if (k.startsWith("_")) {
      continue;
    }

    let inner = dataContext[k];
    if (inner) {
      let finalComponent = k;
      if (!isNaN(parseInt(k))) {
        finalComponent = (parseInt(k) + 1).toString();
      }
      let innerPath = `$${path}${finalComponent}`.replace("$$", "$").replace(/_/g, "-");
      // let finalComponent = k;
      // if (parseInt(finalComponent) + 1) {
      //   finalComponent = (parseInt(finalComponent) + 1).toString();
      // }

      // let innerPath = `$${path}${finalComponent}`.replace("$$", "$").replace(/_/g, "-");

      outputs.push({
        path: innerPath,
        data: "",
      });
    }

    if (typeof inner === "function") {
      inner = await inner();
    }

    if (typeof inner === "object") {
      let o2 = await getVarList(inner, path + k + ".");
      outputs = outputs.concat(o2);
    }

    if (typeof inner === "object" && inner["_keys"]) {
      let keys = await inner["_keys"]();
      // console.log(keys);
      let o = (keys as string[]).map((x) => ({
        path: `$${path}${x}`.replace("$$", "$"),
        data: "monkey",
      }));
      outputs = outputs.concat(o);
    }
  }
  return outputs;
}

function flatten(v: any) {
  if (v === undefined) {
    return "";
  }
  if (v === null) {
    return "";
  }
  if (typeof v === "string") {
    return v;
  }
  if (typeof v === "number") {
    return v.toString();
  }
  if (typeof v === "boolean") {
    return v.toString();
  }
  if (v instanceof Array) {
    return v.map((x) => flatten(x)).join("\n");
  } else {
    return v["_text"] || v["_name"] || v["name"] || v["title"];
  }
}

export async function getVar(path: string, dataContext: any) {
  let pathComponents = path.split(/\.+/g).filter((x) => x);
  pathComponents[0] = pathComponents[0].replace("$", "");
  let v = dataContext;
  for (let pathComponent of pathComponents) {
    let normalizedPathComponent = pathComponent.replace(/-/g, "_") as string | number;
    if (!Number.isNaN(parseInt(pathComponent))) {
      normalizedPathComponent = parseInt(pathComponent) - 1;
    }
    let lastV = v;
    if (v["_get"]) {
      v = v["_get"](pathComponent);
    } else {
      v = v[normalizedPathComponent];
    }

    if (typeof v === "function") {
      v = lastV[normalizedPathComponent] = v();
    }
    if (v && typeof v["then"] === "function") {
      v = await v;
    }
    if (v === undefined) {
      return undefined;
    }
    if (v === null) {
      return "";
    }
    if (typeof v === "string") {
      return v;
    }
    if (typeof v === "number") {
      return v.toString();
    }
    if (typeof v === "boolean") {
      return v.toString();
    }
  }
  // v is an object
  return flatten(v);
}

export interface SyntaxFragment {
  variable: string | null;
  role: "user" | "assistant" | "system";
  isMessageBreak: boolean;
  fragment: string;
  error: string | null;
  promise: Promise<string> | null;
  output: string | null;
  directive: { name: string; options: string } | null;
}

export function parse(prompt: string, dataContext: any) {
  prompt = prompt.replace(/\r/g, "");
  let parts = prompt.split(
    /(\n@[^\n]*|^@[^\n]*|\$\([a-z][-a-z0-9.]*\)|\$[a-z][-a-z0-9.]*[a-z0-9]|\$[a-z]|\n?Assistant:|\n?User:|\n?System:)/g,
  );
  parts = parts.filter((x) => x);
  // console.log(parts);
  let syntax = [] as SyntaxFragment[];
  let role = "user" as "user" | "assistant" | "system";
  for (let i = 0; i < parts.length; i++) {
    let part = parts[i];
    let partTrimmed = part.trim();
    if (part.startsWith("\n@") || (part.startsWith("@") && i == 0)) {
      let start = part.split("@")[0];
      let line = part.split("@")[1];
      let command = line;
      let options = "";
      let spaceIndex = line.indexOf(" ");
      if (spaceIndex != -1) {
        command = line.substring(0, spaceIndex);
        options = line.substring(spaceIndex);
      }

      if (start) {
        syntax.push({
          role,
          isMessageBreak: false,
          variable: null,
          fragment: start,
          promise: null,
          error: null,
          output: null,
          directive: null,
        });
      }

      syntax.push({
        role,
        isMessageBreak: false,
        variable: null,
        fragment: "@" + line,
        promise: null,
        error: null,
        output: "",
        directive: { name: command, options },
      });
    } else if (
      ["Assistant:", "User:", "System:"].includes(partTrimmed) &&
      (part.startsWith("\n") || i == 0)
    ) {
      if (partTrimmed === "Assistant:") {
        role = "assistant";
      } else if (partTrimmed === "User:") {
        role = "user";
      } else if (partTrimmed === "System:") {
        role = "system";
      }
      syntax.push({
        role,
        isMessageBreak: true,
        variable: null,
        fragment: part,
        promise: null,
        error: null,
        output: part,
        directive: null,
      });
    } else if (/^\$[a-z]|^\$\([a-z]/.test(part)) {
      let p = part.split(/(\.+)$/g);
      // The actual variable
      if (p[0]) {
        let fragment = p[0];
        let variable = fragment.replace("$", "").replace("(", "").replace(")", "");
        // let data = getVar(fragment, dataContext);
        let synt: SyntaxFragment = {
          isMessageBreak: false,
          role,
          variable,
          fragment,
          promise: null,
          error: null,
          output: null,
          directive: null,
        };

        // data.then((d) => Vue.set(synt, "error", d === undefined));

        // promise.then((p) => (synt.error = p === undefined));
        syntax.push(synt);
      }
    } else {
      syntax.push({
        role,
        isMessageBreak: false,
        variable: null,
        fragment: part,
        promise: null,
        error: null,
        output: null,
        directive: null,
      });
    }
  }

  return syntax;
}

export async function applyVars(syntax: SyntaxFragment[], dataContext: any) {
  for (let i = 0; i < syntax.length; i++) {
    let synt = syntax[i];
    if (synt.variable) {
      synt.promise = getVar(synt.variable, dataContext);
    }
  }

  for (let i = 0; i < syntax.length; i++) {
    let synt = syntax[i];
    if (synt.promise) {
      try {
        synt.output = await synt.promise;

        if (!!synt.output && synt.output.includes("Error:")) {
          synt.error = synt.output;
        } else if (synt.output === undefined) {
          synt.error = `(unknown variable '${synt.variable}')`;
        } else {
          synt.error = null;
        }
      } catch (ex) {
        synt.error = `(${((ex as any) || "unknown error").toString()})`;
      }
    } else {
      synt.output = synt.fragment;
      synt.error = null;
    }
  }
  return syntax;
}

class SoliloquyDirectiveError implements Error {
  name = "SoliloquyCommandError";
  message = "SoliloquyCommandError";

  constructor(message: string) {
    this.message = message;
  }
}

let models = ["gpt-3.5-turbo", "gpt-4"];
export let temperatures = {
  "precise": 0,
  "balanced": 0.7,
  "creative": 1,
  "super-creative": 1.3,
};

export function syntaxToApiCall(syntax: SyntaxFragment[]) {
  let messages = [] as CompletionMessage[];
  let content = "";
  let role = null as "user" | "assistant" | "system" | null;
  let temperature = 0;
  let model = "gpt-3.5-turbo";
  let error = "";
  let stages = [] as RawChat[];
  let pushContentToMessages = () => {
    // Don't flush if there's nothing to flush - if there's no role set and the content is empty.
    if (messages.length === 0 && content.trim().length === 0 && role === null) {
      return;
    }
    messages.push({ role: role ?? "user", content: content.trim() });
    content = "";
  };

  let pushMessagesToStage = () => {
    stages.push({
      temperature: temperature,
      messages: [...messages],
      model: model,
    });
  };

  let directives = {
    "temp": (params: string) => {
      let t = parseFloat(params.trim());
      if (isNaN(t)) {
        if (temperatures[params.trim()] !== undefined) {
          temperature = temperatures[params.trim()];
          return t;
        }
        throw new SoliloquyDirectiveError("Temp command failed");
      }
      temperature = t;
      return t;
    },
    "model": (params: string) => {
      let m = params.trim();
      if (!m) {
        throw new SoliloquyDirectiveError("No model specified");
      }
      if (!models.includes(m)) {
        throw new SoliloquyDirectiveError(`Model '${m}' not available`);
      }
      model = m;
      return m;
    },
    "Assistant": (params: string) => {
      pushContentToMessages();
      pushMessagesToStage();
      messages = [];
      role = null;
    },
    "gpt-4": (params: string) => {
      model = "gpt-4";
    },
    "gpt-4-turbo": (params: string) => {
      model = "gpt-4-1106-preview";
    },
  };
  let errors = [] as string[];

  for (let synt of syntax) {
    if (synt.error) {
      errors.push(synt.error);
    }

    if (synt.directive) {
      let runDirective = directives[synt.directive.name];
      if (!runDirective) {
        synt.error = `Unknown directive ${synt.directive.name}`;
      } else {
        try {
          runDirective(synt.directive.options);
        } catch (ex: any) {
          synt.error = ex.message;
        }
      }
    } else if (synt.isMessageBreak) {
      pushContentToMessages();
      role = synt.role;
    } else {
      content = content + (synt.error || synt.output);
    }
  }

  pushContentToMessages();
  pushMessagesToStage();

  for (let i = 1; i < stages.length; i++) {
    let lastStage = stages[i - 1];
    let currentStage = stages[i];
    lastStage.continuation = async (convo, response) => {
      currentStage.messages = [...convo.messages, response, ...currentStage.messages];
      currentStage.stream = convo.stream;
      return await chat(currentStage);
    };
  }

  return { processed: stages[0], errors };
}

export default async function processPromptVars(prompt: string, dataContext: any) {
  let syntax = parse(prompt, dataContext);
  await applyVars(syntax, dataContext);
  return syntaxToApiCall(syntax);
}
